import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Organization, Patient } from "fhir/r5";
import { subscribePurchaseReleaseMemory } from "src-next/modules/patient-supporting-documents";
import {
  matchesTransactionOpFilter,
  PATIENT_OP_FILTER_OPTIONS,
  type PatientOpFilter,
} from "../helpers/patientListFilters";
import {
  countTransactionsByOpFilter,
} from "../helpers/patientEcommerceStatus";
import {
  getEcommercePatientKey,
  matchesPatientSearch,
} from "../helpers/patientSearch";
import { ecommercePatientReference } from "../helpers/patientEcommerceOpState";
import { subscribePatientDocStatusCache } from "../helpers/patientDocStatusCache";
import {
  buildDocumentTransactionRows,
  getTransactionRowKey,
} from "../helpers/buildDocumentTransactionRows";
import type { DocumentTransactionRow } from "../helpers/documentTransactions";
import { useEcommercePatients } from "./useEcommercePatients";

export interface UseEcommercePatientsListInitialState {
  searchTerm?: string;
  opFilter?: PatientOpFilter;
}

export function useEcommercePatientsList(
  organization: Organization | null | undefined,
  listRevision = 0,
  initialState?: UseEcommercePatientsListInitialState,
) {
  const {
    cnpj,
    patients,
    loading,
    error,
    resourcesPreloaded,
    preloadFailed,
    dispensesPreloaded,
    isInitialLoading,
    isPreloading,
    isListReady,
    isFullyReady,
    hasFetchedOnce,
    filterTick,
    fetchPatients,
  } = useEcommercePatients(organization, listRevision);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState(initialState?.searchTerm ?? "");
  const [opFilter, setOpFilter] = useState<PatientOpFilter>(
    initialState?.opFilter ?? "all",
  );
  const [cacheTick, setCacheTick] = useState(0);
  const previousCnpjRef = useRef(cnpj);

  useEffect(() => {
    if (previousCnpjRef.current === cnpj) return;
    previousCnpjRef.current = cnpj;
    setSelectedKeys([]);
    setOpFilter("all");
  }, [cnpj]);

  useEffect(() => {
    if (preloadFailed) setOpFilter("all");
  }, [preloadFailed]);

  useEffect(() => {
    const bump = () => setCacheTick((tick) => tick + 1);
    const unsubscribeDocs = subscribePatientDocStatusCache(bump);
    const unsubscribeReleases = subscribePurchaseReleaseMemory(bump);
    return () => {
      unsubscribeDocs();
      unsubscribeReleases();
    };
  }, []);

  // filterTick/cacheTick/resourcesPreloaded forçam recompute a partir dos caches em memória.
  const transactionRows = useMemo(
    () => buildDocumentTransactionRows(patients, cnpj),
    [patients, cnpj, filterTick, cacheTick, resourcesPreloaded],
  );

  const handleTogglePatient = useCallback((key: string) => {
    setSelectedKeys((previous) =>
      previous.includes(key)
        ? previous.filter((selectedKey) => selectedKey !== key)
        : [...previous, key],
    );
  }, []);

  const filteredRows = useMemo(
    () =>
      transactionRows.filter(
        (row) =>
          matchesPatientSearch(row.patient, searchTerm) &&
          matchesTransactionOpFilter(row, opFilter),
      ),
    [transactionRows, searchTerm, opFilter],
  );

  const filteredKeys = useMemo(
    () =>
      filteredRows.map((row) =>
        getTransactionRowKey(
          ecommercePatientReference(row.patient),
          row.transactionId,
        ),
      ),
    [filteredRows],
  );

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedKeys((previous) => [
          ...new Set([...previous, ...filteredKeys]),
        ]);
        return;
      }
      setSelectedKeys((previous) =>
        previous.filter((key) => !filteredKeys.includes(key)),
      );
    },
    [filteredKeys],
  );

  const selectedRows = useMemo(
    () =>
      transactionRows.filter((row) =>
        selectedKeys.includes(
          getTransactionRowKey(
            ecommercePatientReference(row.patient),
            row.transactionId,
          ),
        ),
      ),
    [transactionRows, selectedKeys],
  );

  const selectedPatients = useMemo(() => {
    const unique = new Map<string, Patient>();
    for (const row of selectedRows) {
      unique.set(getEcommercePatientKey(row.patient), row.patient);
    }
    return [...unique.values()];
  }, [selectedRows]);

  const paginationResetKey = useMemo(
    () => `${searchTerm.trim()}|${opFilter}|${filterTick}|${cacheTick}`,
    [searchTerm, opFilter, filterTick, cacheTick],
  );

  const activeFilterOption = PATIENT_OP_FILTER_OPTIONS.find(
    (option) => option.id === opFilter,
  );

  const selectedFilteredCount = filteredKeys.filter((key) =>
    selectedKeys.includes(key),
  ).length;

  const docsPendingCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "docs_pending"),
    [transactionRows],
  );
  const docsRejectedCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "docs_rejected"),
    [transactionRows],
  );
  const docsToApproveCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "docs_to_approve"),
    [transactionRows],
  );
  const awaitingReleaseCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "awaiting_release"),
    [transactionRows],
  );
  const docsReleasedCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "docs_released"),
    [transactionRows],
  );
  const purchaseCompletedCount = useMemo(
    () => countTransactionsByOpFilter(transactionRows, "purchase_completed"),
    [transactionRows],
  );

  const listRefreshing = loading && patients.length > 0;

  const clearSelection = useCallback(() => setSelectedKeys([]), []);

  const getRowKey = useCallback((row: DocumentTransactionRow) => {
    return getTransactionRowKey(
      ecommercePatientReference(row.patient),
      row.transactionId,
    );
  }, []);

  return {
    cnpj,
    patients,
    transactionRows,
    filteredRows,
    loading,
    error,
    resourcesPreloaded,
    preloadFailed,
    dispensesPreloaded,
    fetchPatients,
    selectedKeys,
    searchTerm,
    setSearchTerm,
    opFilter,
    setOpFilter,
    handleTogglePatient,
    handleToggleAll,
    filteredPatients: filteredRows.map((row) => row.patient),
    selectedPatients,
    selectedRows,
    paginationResetKey,
    activeFilterOption,
    selectedFilteredCount,
    docsPendingCount,
    docsRejectedCount,
    docsToApproveCount,
    awaitingReleaseCount,
    docsReleasedCount,
    purchaseCompletedCount,
    isInitialLoading,
    isPreloading,
    isListReady,
    isFullyReady,
    hasFetchedOnce,
    listRefreshing,
    clearSelection,
    getPatientKey: getEcommercePatientKey,
    getRowKey,
  };
}
